import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Handshake, Copy, LogOut, Loader2, Clock, MousePointerClick, Users, Trophy, Wallet,
  CheckCircle2, QrCode, Download, LayoutDashboard, Banknote, FolderOpen, ShieldCheck, Pencil, ChevronDown, BadgeCheck, Sparkles, Trash2,
  Radar, ChevronLeft, ChevronRight, Lock, Unlock,
} from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import AffiliateToolkit, { AffiliateActivity } from '../components/AffiliateToolkit';
import { DailyIdea } from '../components/partner/DailyIdea';
import { RachelGreeting } from '../components/RachelGreeting';
import { useOsTheme, ThemeToggle } from '../os3/theme';
import { TextSizePill } from '../os3/OsFooter';
import { PartnerRadar } from '../components/partner/PartnerRadar';
import { PartnerTier } from '../components/partner/PartnerTier';
import { ProposalGenerator } from '../components/partner/ProposalGenerator';
import { PartnerHero, EvaTip, TodayMission, QuickActions, TodayStats, NearbyRadar } from '../components/partner/CommandCenter';
import { KitxensEffectCard, EstimateDisclaimer } from '../components/KitxensEffect';

function PartnerKitxensEffect({ isEs }) {
  const [period, setPeriod] = useState('year');
  const [d, setD] = useState(null);
  useEffect(() => {
    affFetch(`/me/kitxens-effect?period=${period}`).then((r) => (r.ok ? r.json() : null)).then(setD).catch(() => {});
  }, [period]);
  if (!d?.has_data) return null;
  return (
    <div className="mt-4">
      <KitxensEffectCard d={d} period={period} setPeriod={setPeriod} isEs={isEs} testid="aff-kitxens-effect"
        title={isEs ? 'El efecto Kitxens · tu argumento de venta' : 'The Kitxens effect · your sales pitch'}
        subtitle={isEs ? `Caso real en vivo: ${d.restaurant} (instancia demo). Enséñalo a tus prospectos.` : `Live real case: ${d.restaurant} (demo instance). Show it to your prospects.`} />
    </div>
  );
}
import { TrackingPanel, AttributionFlow, TrustPanel, ReferralJourney } from '../components/partner/Transparency';
import { OpportunityMap } from '../components/partner/OpportunityMap';
import { PartnerLeaderboard } from '../components/partner/PartnerLeaderboard';
import { affFetch } from '../lib/affApi';
import { googleLoginRedirect, consumeGoogleSession, hasGoogleSessionHash, GoogleIcon } from '../lib/googleAuth';
import { AuthShell } from '../components/AuthShell';
import { useT } from '../i18n';

const API = process.env.REACT_APP_BACKEND_URL;

const REF_STATUS = { lead: 'bg-amber-50 text-amber-700 border-amber-200', customer: 'bg-emerald-50 text-emerald-700 border-emerald-200', lost: 'bg-neutral-100 text-neutral-500 border-neutral-200' };
const money = (v) => `$${Number(v || 0).toFixed(2)}`;

/* ---------- Live scans with the partner's ref code ---------- */
function PartnerScans({ isEs }) {
  const [d, setD] = useState(null);
  const [page, setPage] = useState(1);
  useEffect(() => {
    affFetch(`/me/scans?page=${page}`).then((r) => (r.ok ? r.json() : null)).then(setD).catch(() => {});
  }, [page]);
  const ago = (iso) => {
    if (!iso) return '';
    const dte = new Date(iso);
    const days = Math.floor((Date.now() - dte.getTime()) / 86400000);
    const rel = days <= 0 ? (isEs ? 'hoy' : 'today') : days === 1 ? (isEs ? 'ayer' : 'yesterday') : (isEs ? `hace ${days} d` : `${days}d ago`);
    return `${rel} · ${dte.toLocaleDateString(isEs ? 'es-ES' : 'en-US', { day: 'numeric', month: 'short' })}`;
  };
  const tone = (v) => (v >= 65 ? 'text-emerald-600 bg-emerald-50' : v >= 40 ? 'text-[#d8651a] bg-orange-50' : 'text-red-500 bg-red-50');
  return (
    <div className="mt-4 rounded-2xl border border-neutral-200 bg-white p-6" data-testid="aff-scans">
      <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-neutral-500 font-bold">
        <span className="relative inline-flex w-1.5 h-1.5" aria-hidden="true">
          <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
          <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-500" />
        </span>
        <Radar className="w-3.5 h-3.5 text-[#F77F11]" /> {isEs ? 'Scans Index AI con tu código' : 'Index AI scans with your code'}
        {d && <span className="normal-case tracking-normal text-neutral-400">· {d.total} {isEs ? 'en total' : 'total'}</span>}
      </p>
      {!d ? <p className="mt-3 text-[13px] text-neutral-400">…</p> : !d.scans.length ? (
        <p className="mt-3 text-[13px] text-neutral-500">{isEs ? 'Aún no hay scans con tu código — comparte tu link del scanner (Biblioteca → Index AI).' : 'No scans with your code yet — share your scanner link (Assets → Index AI).'}</p>
      ) : (
        <>
          <div className="mt-3 space-y-2">
            {d.scans.map((s, i) => (
              <div key={`${s.domain}-${i}`} className="flex items-center gap-3 rounded-xl bg-neutral-50 border border-neutral-100 px-3.5 py-2.5" data-testid={`aff-scan-${i}`}>
                <span className={`w-11 shrink-0 text-center rounded-lg py-1 text-[13px] font-black tabular-nums ${tone(s.score)}`}>{s.score}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-semibold text-neutral-900 truncate">{s.domain}</p>
                  <p className="text-[11px] text-neutral-500 flex items-center gap-1">
                    {s.unlocked
                      ? <><Unlock className="w-3 h-3 text-emerald-500" /> {isEs ? 'Lead desbloqueado' : 'Lead unlocked'}{s.restaurant && <> · {s.restaurant}</>}</>
                      : <><Lock className="w-3 h-3" /> {isEs ? 'Anónimo — aún sin desbloquear' : 'Anonymous — not unlocked yet'}</>}
                  </p>
                </div>
                <span className="shrink-0 text-[11px] text-neutral-400 tabular-nums">{ago(s.at)}</span>
              </div>
            ))}
          </div>
          {d.pages > 1 && (
            <div className="flex items-center justify-center gap-1.5 mt-4" data-testid="aff-scans-pager">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
                className="w-8 h-8 rounded-lg border border-neutral-200 flex items-center justify-center text-neutral-500 hover:border-[#F77F11] disabled:opacity-30">
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: d.pages }, (_, i) => i + 1).slice(Math.max(0, page - 3), page + 2).map((n) => (
                <button key={n} onClick={() => setPage(n)}
                  className={`w-8 h-8 rounded-lg text-[12.5px] font-bold ${n === page ? 'bg-[#F77F11] text-white' : 'border border-neutral-200 text-neutral-600 hover:border-[#F77F11]'}`}>
                  {n}
                </button>
              ))}
              <button onClick={() => setPage(Math.min(d.pages, page + 1))} disabled={page === d.pages}
                className="w-8 h-8 rounded-lg border border-neutral-200 flex items-center justify-center text-neutral-500 hover:border-[#F77F11] disabled:opacity-30">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ---------- Payout settings (PayPal) ---------- */
function PayoutSettings({ affiliate, isEs, onSaved }) {
  const saved = affiliate.paypal_email || '';
  const [editing, setEditing] = useState(!saved);
  const [pp, setPp] = useState(saved);
  const [confirm, setConfirm] = useState('');
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const validFormat = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(pp.trim());
  const match = pp.trim().toLowerCase() === confirm.trim().toLowerCase() && confirm.trim() !== '';
  const canSave = validFormat && match && agree && !busy;

  const save = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setBusy(true); setErr('');
    try {
      const r = await affFetch('/me/payout-settings', { method: 'POST', body: JSON.stringify({ paypal_email: pp.trim(), confirm_email: confirm.trim() }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(typeof d?.detail === 'string' ? d.detail : (isEs ? 'No se pudo guardar.' : 'Could not save.')); setBusy(false); return; }
      toast.success(isEs ? 'Email de PayPal guardado ✅' : 'PayPal email saved ✅');
      setEditing(false); setConfirm(''); setAgree(false);
      onSaved && onSaved();
    } catch { setErr(isEs ? 'No se pudo guardar.' : 'Could not save.'); }
    setBusy(false);
  };

  const inputCls = 'w-full h-11 rounded-xl bg-white border border-neutral-300 px-3.5 text-[13.5px] text-neutral-900 placeholder-neutral-400 outline-none focus:border-[#F77F11] transition-colors';

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-6" data-testid="aff-payout-settings">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-neutral-500 font-bold">
          <img src="https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_37x23.jpg" alt="PayPal" className="h-5 rounded" />
          {isEs ? 'Método de pago — PayPal' : 'Payout method — PayPal'}
        </p>
        {saved && !editing && (
          <button onClick={() => { setEditing(true); setPp(saved); setConfirm(''); setAgree(false); }}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-neutral-500 hover:text-[#F77F11] transition-colors" data-testid="aff-paypal-edit">
            <Pencil className="w-3.5 h-3.5" /> {isEs ? 'Cambiar' : 'Change'}
          </button>
        )}
      </div>

      {saved && !editing ? (
        <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="aff-paypal-saved">
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 h-10 text-[13.5px] font-semibold text-emerald-700">
            <ShieldCheck className="w-4 h-4" /> {saved}
          </span>
          <p className="text-[12.5px] text-neutral-500">
            {isEs ? 'Tus comisiones se pagarán a esta cuenta cada mes.' : 'Your commissions will be paid to this account every month.'}
          </p>
        </div>
      ) : (
        <form onSubmit={save} className="mt-4 max-w-md">
          <p className="text-[13px] text-neutral-600 leading-relaxed">
            {isEs
              ? 'Escribe el email de tu cuenta PayPal para recibir tus comisiones. Liquidación mensual, sin mínimos ocultos.'
              : 'Enter the email of your PayPal account to receive your commissions. Monthly settlement, no hidden minimums.'}
          </p>
          <input type="email" value={pp} onChange={(e) => { setPp(e.target.value); setErr(''); }}
            placeholder={isEs ? 'Email de PayPal' : 'PayPal email'} className={`mt-3 ${inputCls}`} data-testid="aff-paypal-input" />
          {pp.trim() !== '' && !validFormat && (
            <p className="mt-1 text-[11.5px] text-red-500">{isEs ? 'Formato de email no válido.' : 'Invalid email format.'}</p>
          )}
          <input type="email" value={confirm} onChange={(e) => { setConfirm(e.target.value); setErr(''); }}
            placeholder={isEs ? 'Confirma el email' : 'Confirm email'} className={`mt-3 ${inputCls}`} data-testid="aff-paypal-confirm-input" />
          {confirm.trim() !== '' && !match && (
            <p className="mt-1 text-[11.5px] text-red-500">{isEs ? 'Los emails no coinciden.' : 'Emails do not match.'}</p>
          )}
          <label className="mt-3 flex items-start gap-2.5 cursor-pointer select-none">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#F77F11]" data-testid="aff-paypal-agree" />
            <span className="text-[12.5px] text-neutral-600 leading-relaxed">
              {isEs ? 'Confirmo que este email pertenece a mi cuenta de PayPal activa y puedo recibir pagos en ella.' : 'I confirm this email belongs to my active PayPal account and I can receive payments on it.'}
            </span>
          </label>
          {err && <p className="mt-2 text-[12.5px] text-red-500" data-testid="aff-paypal-error">{err}</p>}
          <div className="mt-4 flex gap-2">
            <button type="submit" disabled={!canSave}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#0070BA] hover:bg-[#005c99] disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-[13.5px] transition-colors"
              data-testid="aff-paypal-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              {isEs ? 'Guardar PayPal' : 'Save PayPal'}
            </button>
            {saved && (
              <button type="button" onClick={() => setEditing(false)}
                className="h-11 px-5 rounded-full border border-neutral-300 text-[13px] font-semibold text-neutral-600 hover:border-neutral-500 transition-colors" data-testid="aff-paypal-cancel">
                {isEs ? 'Cancelar' : 'Cancel'}
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}

/* ---------- QR + Partner Kit download ---------- */
function PartnerKit({ link, refCode, name, isEs }) {
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=480x480&margin=12&color=1a1a1a&data=${encodeURIComponent(link)}`;
  const downloadQr = async () => {
    try {
      const blob = await (await fetch(qrUrl)).blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `kitxens-partner-qr-${refCode}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(isEs ? 'QR descargado ✅' : 'QR downloaded ✅');
    } catch { window.open(qrUrl, '_blank'); }
  };
  const downloadKit = () => {
    const kit = isEs
      ? `KITXENS PARTNER KIT — ${name}\n${'='.repeat(40)}\n\nTU ENLACE: ${link}\nTU CÓDIGO: ${refCode}\n\nGANAS: 30% recurrente de todo lo que contrate tu referido.\nTU REFERIDO GANA: 10% de descuento durante 1 año.\nCOOKIE: 90 días.\n\n--- MENSAJE WHATSAPP ---\nHola 👋 ¿Sabías que 8 de cada 10 comensales eligen restaurante entre los 3 primeros resultados de Google o ChatGPT? KITXENS audita tu restaurante GRATIS y te muestra dónde pierdes ventas. Con mi enlace tienes 10% de descuento 1 año: ${link}\n\n--- EMAIL CORTO ---\nAsunto: Tu restaurante, invisible para la IA\n\nHola {nombre},\n\nLos comensales ya buscan en ChatGPT y Google AI. Si tu menú está en PDF, eres invisible. KITXENS lo arregla: auditoría gratis + 10% de descuento con mi enlace: ${link}\n\nUn saludo,\n${name}\n\n--- BIO / REDES ---\n🍽️ Partner de @kitxens — Restaurant Operating Intelligence. Auditoría IA gratis para tu restaurante → ${link}\n`
      : `KITXENS PARTNER KIT — ${name}\n${'='.repeat(40)}\n\nYOUR LINK: ${link}\nYOUR CODE: ${refCode}\n\nYOU EARN: 30% recurring on everything your referral contracts.\nYOUR REFERRAL GETS: 10% off for 1 year.\nCOOKIE: 90 days.\n\n--- WHATSAPP MESSAGE ---\nHey 👋 Did you know 8 out of 10 diners pick restaurants from the top 3 results on Google or ChatGPT? KITXENS audits your restaurant for FREE and shows where you're losing sales. Use my link for 10% off for 1 year: ${link}\n\n--- SHORT EMAIL ---\nSubject: Your restaurant is invisible to AI\n\nHi {name},\n\nDiners now search on ChatGPT and Google AI. If your menu lives in a PDF, you're invisible. KITXENS fixes that: free audit + 10% off with my link: ${link}\n\nBest,\n${name}\n\n--- SOCIAL BIO ---\n🍽️ @kitxens partner — Restaurant Operating Intelligence. Free AI audit for your restaurant → ${link}\n`;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([kit], { type: 'text/plain;charset=utf-8' }));
    a.download = `kitxens-partner-kit-${refCode}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(isEs ? 'Partner Kit descargado ✅' : 'Partner Kit downloaded ✅');
  };
  return (
    <div className="mt-6 rounded-2xl border border-neutral-200 bg-white p-5 flex flex-wrap items-center gap-5" data-testid="aff-partner-kit">
      <img src={qrUrl.replace('480x480', '160x160')} alt="QR" className="w-24 h-24 rounded-xl border border-neutral-200 p-1.5 shrink-0" data-testid="aff-qr-img" />
      <div className="flex-1 min-w-[220px]">
        <p className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-neutral-500 font-bold"><QrCode className="w-3.5 h-3.5 text-[#F77F11]" /> Partner Kit</p>
        <p className="mt-1 text-[13px] text-neutral-600 leading-relaxed">
          {isEs ? 'Tu QR apunta a tu enlace de referido. Descarga el kit: mensajes listos para WhatsApp, email y redes — promociona en 2 minutos.' : 'Your QR points to your referral link. Download the kit: ready-made WhatsApp, email and social messages — start promoting in 2 minutes.'}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={downloadQr} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 hover:border-[#F77F11] px-4 h-10 text-[12.5px] font-semibold text-neutral-700 transition-colors" data-testid="aff-download-qr">
          <Download className="w-3.5 h-3.5" /> QR
        </button>
        <button onClick={downloadKit} className="inline-flex items-center gap-1.5 rounded-full bg-[#F77F11] hover:bg-[#d8651a] px-4 h-10 text-[12.5px] font-semibold text-white transition-colors" data-testid="aff-download-kit">
          <Download className="w-3.5 h-3.5" /> {isEs ? 'Descargar Kit' : 'Download Kit'}
        </button>
      </div>
    </div>
  );
}

/* ---------- Email signature generator: official, linked and trackable ---------- */
function EmailSignature({ isEs, affiliate }) {
  const site = 'https://www.kitxens.com';
  const code = affiliate.ref_code;
  const scanLink = `${site}/index-ai?ref=${code}`;
  const siteLink = `${site}/?ref=${code}`;
  const firstName = (affiliate.name || '').split(' ')[0];
  const sigHtml = `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;border-collapse:collapse">
  <tr>
    <td style="padding-right:14px;border-right:3px solid #F77F11;vertical-align:top">
      <a href="${siteLink}" target="_blank" style="text-decoration:none">
        <img src="${site}/kitxens-logo.png" width="52" height="52" alt="Kitxens" style="display:block;border-radius:10px"/>
      </a>
    </td>
    <td style="padding-left:14px;vertical-align:top">
      <p style="margin:0;font-size:15px;font-weight:bold;color:#16181D">${affiliate.name || ''}</p>
      <p style="margin:2px 0 0;font-size:11px;letter-spacing:1.5px;font-weight:bold;color:#F77F11">✓ KITXENS CERTIFIED PARTNER</p>
      <p style="margin:8px 0 0;font-size:12.5px;color:#374151">
        ${isEs ? '¿Tu restaurante aparece en ChatGPT y Google?' : 'Does your restaurant show up on ChatGPT & Google?'}<br/>
        <a href="${scanLink}" target="_blank" style="color:#F77F11;font-weight:bold;text-decoration:none">🔍 ${isEs ? 'Escanéalo gratis en 30 segundos' : 'Scan it free in 30 seconds'} →</a>
      </p>
      <p style="margin:6px 0 0;font-size:11px;color:#9CA3AF">
        <a href="${siteLink}" target="_blank" style="color:#9CA3AF;text-decoration:none">kitxens.com</a> · ${isEs ? 'Inteligencia Operativa para Restaurantes' : 'Restaurant Operating Intelligence'}
      </p>
    </td>
  </tr>
</table>`;
  const copySignature = async () => {
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new window.ClipboardItem({
          'text/html': new Blob([sigHtml], { type: 'text/html' }),
          'text/plain': new Blob([`${affiliate.name} — Kitxens Certified Partner\n${scanLink}`], { type: 'text/plain' }),
        })]);
      } else {
        await navigator.clipboard.writeText(sigHtml);
      }
      toast.success(isEs ? 'Firma copiada — pégala en Gmail/Outlook ✅' : 'Signature copied — paste it into Gmail/Outlook ✅');
    } catch {
      try {
        await navigator.clipboard.writeText(sigHtml);
        toast.success(isEs ? 'Firma copiada (HTML) — pégala en tu gestor de firmas ✅' : 'Signature copied (HTML) — paste it into your signature manager ✅');
      } catch {
        toast.error(isEs ? 'No se pudo copiar automáticamente' : 'Could not copy automatically');
      }
    }
  };
  return (
    <div className="mt-5 rounded-2xl bg-white border border-neutral-200 p-6 sm:p-8" data-testid="aff-email-signature">
      <p className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] font-bold uppercase text-[#F77F11]">
        <Pencil className="w-3.5 h-3.5" /> {isEs ? 'Tu firma de email oficial' : 'Your official email signature'}
      </p>
      <h2 className="mt-2 text-lg font-bold text-neutral-900">
        {isEs ? `Cada email que envías, vende por ti, ${firstName}` : `Every email you send sells for you, ${firstName}`}
      </h2>
      <p className="mt-1 text-[13px] text-neutral-500 max-w-2xl leading-relaxed">
        {isEs
          ? 'Cópiala y pégala en Gmail (Ajustes → Firma) u Outlook. Todos los enlaces llevan tu código y cada clic y lead queda registrado a tu nombre durante 90 días.'
          : 'Copy and paste it into Gmail (Settings → Signature) or Outlook. Every link carries your code, and each click and lead is credited to you for 90 days.'}
      </p>
      <div className="mt-5 rounded-xl border border-dashed border-neutral-300 bg-neutral-50/60 p-5 overflow-x-auto">
        <div dangerouslySetInnerHTML={{ __html: sigHtml }} data-testid="signature-preview" />
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button onClick={copySignature}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#F77F11] hover:bg-[#d8651a] px-5 h-10 text-[13px] font-bold text-white transition-colors"
          data-testid="signature-copy-btn">
          <Copy className="w-4 h-4" /> {isEs ? 'Copiar mi firma' : 'Copy my signature'}
        </button>
        <span className="text-[12px] text-neutral-400">
          {isEs ? `Rastreable con tu código ${code} — lo verás todo en tu radar y comisiones.` : `Trackable with your code ${code} — you'll see everything in your radar and commissions.`}
        </span>
      </div>
    </div>
  );
}

/* ---------- Official brand kit: downloadable logo + certified partner badge ---------- */
function BrandKit({ isEs }) {
  const assets = [
    { file: '/brand/kitxens-logo-square-light.png', name: isEs ? 'Logo — cuadrado claro (PNG)' : 'Logo — square light (PNG)', dl: 'kitxens-logo-square-light.png' },
    { file: '/brand/kitxens-logo-square-dark.png', name: isEs ? 'Logo — cuadrado oscuro (PNG)' : 'Logo — square dark (PNG)', dl: 'kitxens-logo-square-dark.png', dark: true },
    { file: '/brand/kitxens-logo-round-light.png', name: isEs ? 'Logo — redondo claro (PNG)' : 'Logo — round light (PNG)', dl: 'kitxens-logo-round-light.png' },
    { file: '/brand/kitxens-logo-round-transparent.png', name: isEs ? 'Logo — redondo transparente (PNG)' : 'Logo — round transparent (PNG)', dl: 'kitxens-logo-round-transparent.png' },
    { file: '/brand/kitxens-logo-wordmark-dark.svg', name: isEs ? 'Wordmark oscuro (SVG)' : 'Wordmark dark (SVG)', dl: 'kitxens-logo-wordmark-dark.svg' },
    { file: '/brand/kitxens-logo-wordmark-light.svg', name: isEs ? 'Wordmark claro (SVG)' : 'Wordmark light (SVG)', dl: 'kitxens-logo-wordmark-light.svg', dark: true },
    { file: '/brand/kitxens-certified-partner.svg', name: isEs ? 'Badge Partner — oscuro (SVG)' : 'Partner badge — dark (SVG)', dl: 'kitxens-certified-partner.svg', dark: true },
    { file: '/brand/kitxens-certified-partner-light.svg', name: isEs ? 'Badge Partner — claro (SVG)' : 'Partner badge — light (SVG)', dl: 'kitxens-certified-partner-light.svg' },
    { file: '/brand/kitxens-certified-partner-round.svg', name: isEs ? 'Badge Partner — redondo (SVG)' : 'Partner badge — round (SVG)', dl: 'kitxens-certified-partner-round.svg', dark: true },
    { file: '/brand/kitxens-certified-partner-transparent.svg', name: isEs ? 'Badge Partner — transparente (SVG)' : 'Partner badge — transparent (SVG)', dl: 'kitxens-certified-partner-transparent.svg' },
  ];
  return (
    <div className="mt-5 rounded-2xl bg-white border border-neutral-200 p-6 sm:p-8" data-testid="aff-brand-kit">
      <p className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] font-bold uppercase text-[#F77F11]">
        <BadgeCheck className="w-3.5 h-3.5" /> {isEs ? 'Kit de marca oficial' : 'Official brand kit'}
      </p>
      <h2 className="mt-2 text-lg font-bold text-neutral-900">{isEs ? 'Comparte siempre con la marca oficial' : 'Always share with the official brand'}</h2>
      <p className="mt-1 text-[13px] text-neutral-500 max-w-2xl leading-relaxed">
        {isEs
          ? 'Descarga el logo (PNG/SVG, claro/oscuro, redondo/cuadrado) y tu badge de Kitxens Certified Partner para propuestas, firma de email, web y redes.'
          : 'Download the logo (PNG/SVG, light/dark, round/square) and your Kitxens Certified Partner badge for proposals, email signature, website and social.'}
      </p>
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {assets.map((a) => (
          <div key={a.file} className="rounded-xl border border-neutral-200 p-3 flex flex-col">
            <div className={`h-20 rounded-lg border border-neutral-100 flex items-center justify-center overflow-hidden ${a.dark ? 'bg-neutral-900' : 'bg-neutral-50'}`}>
              <img src={a.file} alt={a.name} className="max-h-16 max-w-[85%] object-contain" loading="lazy" />
            </div>
            <p className="mt-2.5 text-[11.5px] font-semibold text-neutral-800 leading-snug flex-1">{a.name}</p>
            <a href={a.file} download={a.dl}
              className="mt-2.5 inline-flex items-center justify-center gap-1.5 rounded-full bg-neutral-950 hover:bg-neutral-800 text-white h-8 text-[11.5px] font-bold transition-colors"
              data-testid={`brand-download-${a.dl.replace(/\./g, '-')}`}>
              <Download className="w-3 h-3" /> {isEs ? 'Descargar' : 'Download'}
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- The partner's 3 flagship product links ---------- */
function DemoRadar({ isEs, refCode, stats }) {
  const visits = stats?.demo_visits ?? 0;
  const signups = stats?.demo_signups ?? 0;
  const link = `https://www.kitxens.com/demo?ref=${refCode}`;
  const nextMilestone = signups < 5 ? 5 : Math.ceil((signups + 1) / 5) * 5;
  const toGo = nextMilestone - signups;
  const pct = Math.min(100, Math.round((signups / nextMilestone) * 100));
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(link); toast.success(isEs ? 'Link de demo copiado — a compartir' : 'Demo link copied — go share it'); }
    catch { toast.error(isEs ? 'No se pudo copiar' : 'Could not copy'); }
  };
  return (
    <div className="mt-4 rounded-2xl border border-neutral-200 bg-white p-5" data-testid="aff-demo-radar">
      <div className="flex items-center gap-2">
        <Radar className="w-4 h-4 text-[#F77F11]" />
        <p className="text-[13px] font-bold text-neutral-900">{isEs ? 'Radar de demos' : 'Demo radar'}</p>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-neutral-50 border border-neutral-100 p-3">
          <p className="text-xl font-bold text-neutral-900" data-testid="aff-demo-visits">{visits}</p>
          <p className="text-[11px] text-neutral-500 font-medium">{isEs ? 'visitas con tu link' : 'visits from your link'}</p>
        </div>
        <div className="rounded-xl bg-orange-50/60 border border-orange-100 p-3">
          <p className="text-xl font-bold text-[#F77F11]" data-testid="aff-demo-signups">{signups}</p>
          <p className="text-[11px] text-neutral-500 font-medium">{isEs ? 'registros a tu nombre' : 'signups credited to you'}</p>
        </div>
      </div>
      <div className="mt-3">
        <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
          <div className="h-full rounded-full bg-[#F77F11] transition-all duration-700" style={{ width: `${pct}%` }} />
        </div>
        <p className="mt-2 text-[12px] text-neutral-600">
          {isEs
            ? <>{toGo} {toGo === 1 ? 'registro' : 'registros'} más y alcanzas tu próximo hito de <b>{nextMilestone}</b> — cada registro es un lead con tu nombre en nuestro CRM.</>
            : <>{toGo} more {toGo === 1 ? 'signup' : 'signups'} to reach your next milestone of <b>{nextMilestone}</b> — every signup is a lead credited to you in our CRM.</>}
        </p>
      </div>
      <button onClick={copyLink} data-testid="aff-demo-radar-copy"
        className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-neutral-900 text-white text-[12.5px] font-bold px-4 py-2.5 hover:bg-neutral-800 transition-colors">
        <Copy className="w-3.5 h-3.5" /> {isEs ? 'Copiar mi link de demo y compartir' : 'Copy my demo link & share'}
      </button>
    </div>
  );
}

function IndexAiScanPromo({ isEs, refCode }) {
  const scanLink = `https://www.kitxens.com/index-ai?ref=${refCode}`;
  const productLinks = [
    { id: 'indexai', name: 'Index AI™', price: 99, link: scanLink, note: isEs ? 'Scanner gratuito — tu mejor apertura' : 'Free scanner — your best door-opener' },
    { id: 'workforce', name: 'AI Workforce™', price: 199, link: `https://www.kitxens.com/ai-agents-for-restaurants?ref=${refCode}`, note: isEs ? 'Empleados IA 24/7 (Rachel y equipo)' : 'AI employees 24/7 (Rachel & team)' },
    { id: 'catering', name: 'Catering AI™', price: 399, link: `https://www.kitxens.com/ai/catering-ai?ref=${refCode}`, note: isEs ? 'Plataforma de catering corporativo' : 'Corporate catering platform' },
  ];
  const copyLink = async (link, name) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success(isEs ? `Link de ${name} copiado ✅` : `${name} link copied ✅`);
    } catch {
      toast.error(isEs ? 'No se pudo copiar — selecciona el link manualmente' : 'Could not copy — select the link manually');
    }
  };
  const steps = isEs
    ? [
        { n: '1', t: 'Comparte tu link del scanner', d: 'Por WhatsApp, email o en persona: el restaurante escanea su web gratis en 30 segundos.' },
        { n: '2', t: 'El scan hace la venta por ti', d: 'Ve su Index AI Score™ real, los problemas detectados y desbloquea su informe dejando sus datos.' },
        { n: '3', t: 'El lead queda a tu nombre', d: 'Aparece en tu radar, Kitxens lo trabaja y cierra — tú cobras tu 30% recurrente.' },
      ]
    : [
        { n: '1', t: 'Share your scanner link', d: 'Via WhatsApp, email or in person: the restaurant scans its website free in 30 seconds.' },
        { n: '2', t: 'The scan sells for you', d: 'They see their real Index AI Score™, the issues found, and unlock the report with their contact info.' },
        { n: '3', t: 'The lead is credited to you', d: 'It shows on your radar, Kitxens works and closes it — you earn your 30% recurring.' },
      ];
  return (
    <div className="mt-5 rounded-2xl bg-neutral-950 text-white p-6 sm:p-8 relative overflow-hidden" data-testid="aff-indexai-promo">
      <div aria-hidden="true" className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#F77F11]/15 blur-[90px] pointer-events-none" />
      <div className="relative">
        <p className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] font-bold uppercase text-[#FF9B4A]">
          <Sparkles className="w-3.5 h-3.5" /> {isEs ? 'Tus 3 productos estrella — listos para vender' : 'Your 3 flagship products — ready to sell'}
        </p>
        <h2 className="mt-2 text-xl sm:text-2xl font-bold tracking-[-0.01em]">Index AI™ · AI Workforce™ · Catering AI™</h2>
        <p className="mt-1.5 text-[13.5px] text-neutral-400 max-w-2xl leading-relaxed">
          {isEs
            ? 'Empieza siempre por el scanner gratuito de Index AI: muestra al restaurante lo invisible que es para ChatGPT y Google. Después, escala la conversación hacia agentes IA y catering.'
            : 'Always open with the free Index AI scanner: it shows the restaurant how invisible it is to ChatGPT and Google. Then scale the conversation into AI agents and catering.'}
        </p>
        <div className="mt-5 grid sm:grid-cols-3 gap-3">
          {steps.map((s) => (
            <div key={s.n} className="rounded-xl bg-white/[0.05] border border-white/10 p-4">
              <span className="w-6 h-6 rounded-full bg-[#F77F11] text-white text-[12px] font-bold flex items-center justify-center">{s.n}</span>
              <p className="mt-2.5 font-bold text-[13.5px]">{s.t}</p>
              <p className="mt-1 text-[12.5px] text-neutral-400 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-5 space-y-2.5">
          {productLinks.map((p) => (
            <div key={p.id} className="flex flex-col sm:flex-row sm:items-center gap-2.5">
              <div className="sm:w-56 shrink-0">
                <p className="font-bold text-[13.5px]">{p.name} {p.price != null && <span className="text-neutral-500 font-medium">· {isEs ? `desde $${p.price}/mes` : `from $${p.price}/mo`}</span>}</p>
                <p className="text-[11.5px] text-neutral-500">{p.note}</p>
              </div>
              <code className="flex-1 truncate rounded-xl bg-white/[0.06] border border-white/10 px-4 py-2.5 text-[12.5px] text-[#FF9B4A]" data-testid={`aff-product-link-${p.id}`}>{p.link}</code>
              <button onClick={() => copyLink(p.link, p.name)} className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#F77F11] hover:bg-[#d8651a] px-4 h-10 text-[12.5px] font-bold text-white transition-colors shrink-0" data-testid={`aff-product-copy-${p.id}`}>
                <Copy className="w-3.5 h-3.5" /> {isEs ? 'Copiar' : 'Copy'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Sales templates + sector intelligence ---------- */
function SalesToolkit({ isEs, link, name, refCode }) {
  const [feed, setFeed] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/intelligence-feed?limit=6`).then((r) => r.json()).then((d) => setFeed(d.items || [])).catch(() => {});
  }, []);

  const scanLink = `https://www.kitxens.com/index-ai?ref=${refCode}`;
  const wfLink = `https://www.kitxens.com/ai-agents-for-restaurants?ref=${refCode}`;
  const catLink = `https://www.kitxens.com/ai/catering-ai?ref=${refCode}`;

  const templates = isEs ? [
    { id: 'wa-indexai', label: '⚡ WhatsApp — Index AI™ (el mejor abridor)', text: `Hola {nombre} 👋 ¿Sabes si tu restaurante aparece cuando alguien le pregunta a ChatGPT dónde comer? La mayoría no — y ahí se van clientes cada día.\n\nEscanea tu web gratis en 30 segundos y ve tu nota real: ${scanLink}\n\nSi sale baja, te cuento cómo arreglarlo (desde $99/mes y con mi enlace 10% dto 1 año).` },
    { id: 'wa-workforce', label: '🤖 WhatsApp — AI Workforce™', text: `Hola {nombre} 👋 ¿Cuántas llamadas pierde tu restaurante en hora punta? Rachel es una empleada de IA que contesta llamadas, reseñas y reservas 24/7 — nunca libra ni llega tarde 😄\n\nMírala en acción: ${wfLink}\n\nDesde $199/mes, y con mi enlace tienes 10% dto durante 1 año.` },
    { id: 'wa-catering', label: '🍽 WhatsApp — Catering AI™ (si ya hacen eventos)', text: `Hola {nombre} 👋 Vi que ya hacéis eventos y catering. ¿Y si cada empresa de tu zona pudiera pedirte presupuesto online y la IA lo gestionara sola — menús, precios y seguimiento?\n\nEso es Catering AI de KITXENS: ${catLink}\n\nDesde $399/mes. Con mi enlace, 10% dto 1 año. ¿Te enseño una demo?` },
    { id: 'email', label: '📧 Email de presentación (general)', text: `Hola {nombre},\n\nSoy ${name} y trabajo con KITXENS, una plataforma de inteligencia operativa para restaurantes (IA para que te encuentren en Google y ChatGPT, POS, pedidos online y agentes de IA).\n\nLo interesante: auditan tu restaurante gratis y te dicen exactamente dónde estás perdiendo ventas. Y por venir de mi parte tienes un 10% de descuento durante 1 año.\n\nÉchale un vistazo aquí: ${link}\n\n¿Te va bien una llamada esta semana?` },
    { id: 'newsletter', label: '📰 Bloque para tu newsletter', text: `🍽 RECOMENDACIÓN DEL MES\n\nSi tienes un restaurante, esto te interesa: la IA ya decide qué restaurantes se recomiendan en Google y ChatGPT. KITXENS es un "sistema operativo" de inteligencia para restaurantes: auditoría gratuita, visibilidad en búsquedas con IA, POS y automatización.\n\nCon nuestro enlace de partner obtienes un 10% de descuento durante un año:\n${link}` },
  ] : [
    { id: 'wa-indexai', label: '⚡ WhatsApp — Index AI™ (best door-opener)', text: `Hey {name} 👋 Do you know if your restaurant shows up when someone asks ChatGPT where to eat? Most don't — and that's customers walking away every day.\n\nScan your website free in 30 seconds and see your real score: ${scanLink}\n\nIf it's low, I'll show you how to fix it (from $99/mo, and 10% off for a year with my link).` },
    { id: 'wa-workforce', label: '🤖 WhatsApp — AI Workforce™', text: `Hey {name} 👋 How many calls does your restaurant miss during the rush? Rachel is an AI employee who answers calls, reviews and reservations 24/7 — never calls in sick 😄\n\nSee her in action: ${wfLink}\n\nFrom $199/mo, and my link gets you 10% off for a year.` },
    { id: 'wa-catering', label: '🍽 WhatsApp — Catering AI™ (if they already do events)', text: `Hey {name} 👋 I saw you already do events and catering. What if every company in your area could request a quote online — and AI handled menus, pricing and follow-up on its own?\n\nThat's KITXENS Catering AI: ${catLink}\n\nFrom $399/mo. 10% off for a year with my link. Want a quick demo?` },
    { id: 'email', label: '📧 Intro email (general)', text: `Hi {name},\n\nI'm ${name}, working with KITXENS — a Restaurant Operating Intelligence platform (AI visibility on Google & ChatGPT, POS, online ordering and AI agents).\n\nThe interesting part: they audit your restaurant for free and show you exactly where you're losing sales. And through me you get 10% off for a full year.\n\nTake a look: ${link}\n\nWould a quick call this week work?` },
    { id: 'newsletter', label: '📰 Newsletter block', text: `🍽 THIS MONTH'S PICK\n\nIf you run a restaurant, this matters: AI now decides which restaurants get recommended on Google and ChatGPT. KITXENS is a Restaurant Operating Intelligence system: free audit, AI search visibility, POS and automation.\n\nUse our partner link for 10% off for a year:\n${link}` },
  ];

  const c = isEs
    ? { title: 'Sales Toolkit', sub: 'Plantillas listas para copiar — tu enlace ya va incluido.', copy: 'Copiar', copied: 'Plantilla copiada ✅', intel: 'Munición de ventas · Inteligencia del sector', intelSub: 'Comparte estas noticias con tus contactos para abrir conversaciones.' }
    : { title: 'Sales Toolkit', sub: 'Ready-to-copy templates — your link is already included.', copy: 'Copy', copied: 'Template copied ✅', intel: 'Sales ammo · Sector intelligence', intelSub: 'Share these stories with your contacts to open conversations.' };

  return (
    <div className="mt-6 grid lg:grid-cols-2 gap-6" data-testid="aff-toolkit">
      <div className="rounded-2xl border border-neutral-200 bg-white p-6">
        <p className="text-[11px] uppercase tracking-wider text-[#F77F11] font-bold">🧰 {c.title}</p>
        <p className="mt-1 text-[12px] text-neutral-500">{c.sub}</p>
        <div className="mt-4 space-y-3">
          {templates.map((t) => (
            <div key={t.id} className="rounded-xl bg-neutral-50 border border-neutral-200 p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-[13px] font-semibold text-neutral-900">{t.label}</p>
                <button onClick={() => { navigator.clipboard.writeText(t.text); toast.success(c.copied); }}
                  className="inline-flex items-center gap-1 rounded-full border border-neutral-300 hover:border-[#F77F11] px-3 h-7 text-[11px] font-semibold text-neutral-600 transition-colors" data-testid={`toolkit-copy-${t.id}`}>
                  <Copy className="w-3 h-3" /> {c.copy}
                </button>
              </div>
              <p className="mt-2 text-[11.5px] text-neutral-500 leading-relaxed line-clamp-3 whitespace-pre-line">{t.text}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-2xl border border-neutral-200 bg-white p-6" data-testid="aff-intel-feed">
        <p className="text-[11px] uppercase tracking-wider text-[#F77F11] font-bold">📡 {c.intel}</p>
        <p className="mt-1 text-[12px] text-neutral-500">{c.intelSub}</p>
        <div className="mt-4 space-y-2">
          {feed.length === 0 ? <p className="text-[12px] text-neutral-400">…</p> : feed.map((it) => (
            <a key={it.id || it.url} href={it.url} target="_blank" rel="noopener noreferrer"
              className="block rounded-xl bg-neutral-50 border border-neutral-200 px-4 py-3 hover:border-[#F77F11]/60 transition-colors">
              <p className="text-[12.5px] font-medium text-neutral-800 leading-snug">{isEs && it.title_es ? it.title_es : it.title}</p>
              <p className="mt-0.5 text-[10.5px] text-neutral-500 uppercase tracking-wide">{it.source}</p>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Small building blocks ---------- */
function StatCards({ cards, testId }) {
  return (
    <div className={`grid gap-2.5 sm:gap-3 ${cards.length % 3 === 0 ? 'grid-cols-3' : 'grid-cols-2 md:grid-cols-3'}`} data-testid={testId}>
      {cards.map((s) => (
        <div key={s.l} className="rounded-2xl border border-neutral-200 bg-white p-3.5 sm:p-4">
          <s.icon className="w-4 h-4 text-[#F77F11]" />
          <p className="mt-2 text-lg sm:text-xl font-bold tabular-nums text-neutral-900">{s.v}</p>
          <p className="mt-0.5 text-[10px] sm:text-[10.5px] uppercase tracking-wider text-neutral-500 font-bold leading-tight">{s.l}</p>
        </div>
      ))}
    </div>
  );
}

function LinkBox({ link, c }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-5 flex flex-wrap items-center gap-4" data-testid="aff-link-box">
      <div className="flex-1 min-w-[240px]">
        <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">{c.yourLink}</p>
        <p className="mt-1 font-mono text-[14px] text-[#F77F11] break-all" data-testid="aff-ref-link">{link}</p>
      </div>
      <button onClick={() => { navigator.clipboard.writeText(link); toast.success(c.copied); }}
        className="inline-flex items-center gap-1.5 rounded-full bg-[#F77F11] hover:bg-[#d8651a] px-5 h-10 text-[12.5px] font-semibold text-white transition-colors" data-testid="aff-copy-link">
        <Copy className="w-3.5 h-3.5" /> {c.copy}
      </button>
    </div>
  );
}

/* ---------- Referrals with transparency timeline ---------- */
const EVENT_LABELS = (isEs) => isEs ? {
  lead_registered: 'Lead registrado y atribuido a tu código',
  contacted: 'Contactado por el equipo Kitxens',
  proposal_sent: 'Propuesta enviada',
  demo: 'Demo / reunión realizada',
  follow_up: 'Seguimiento realizado',
  note: 'Actualización del equipo',
  status_customer: '🎉 ¡Convertido en cliente!',
  status_lost: 'Marcado como perdido',
  status_lead: 'Reactivado como lead',
} : {
  lead_registered: 'Lead registered and attributed to your code',
  contacted: 'Contacted by the Kitxens team',
  proposal_sent: 'Proposal sent',
  demo: 'Demo / meeting held',
  follow_up: 'Follow-up done',
  note: 'Team update',
  status_customer: '🎉 Converted to customer!',
  status_lost: 'Marked as lost',
  status_lead: 'Reactivated as lead',
};

function ReferralTimeline({ rid, isEs }) {
  const [tl, setTl] = useState(null);
  const labels = EVENT_LABELS(isEs);
  useEffect(() => {
    affFetch(`/me/referrals/${rid}/timeline`).then((r) => r.ok && r.json().then(setTl)).catch(() => {});
  }, [rid]);
  if (!tl) return <p className="px-3.5 py-2 text-[12px] text-neutral-400">…</p>;
  return (
    <div data-testid={`aff-ref-timeline-${rid}`}>
      <ReferralJourney events={tl.events} isEs={isEs} />
      <div className="px-4 pb-3 pt-2">
      {tl.events.map((e, i) => (
        <div key={i} className="relative pl-5 pb-3 last:pb-0">
          {i < tl.events.length - 1 && <span className="absolute left-[5px] top-3 bottom-0 w-px bg-neutral-200" />}
          <span className={`absolute left-0 top-1.5 w-[11px] h-[11px] rounded-full border-2 border-white ${e.type === 'status_customer' ? 'bg-emerald-500' : e.type === 'status_lost' ? 'bg-neutral-300' : 'bg-[#F77F11]'}`} />
          <p className="text-[12.5px] font-semibold text-neutral-800 leading-tight">{labels[e.type] || e.type}</p>
          {e.note && <p className="text-[12px] text-neutral-500 mt-0.5">{e.note}</p>}
          <p className="text-[10.5px] text-neutral-400 mt-0.5 tabular-nums">{new Date(e.at).toLocaleDateString()} · {new Date(e.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
        </div>
      ))}
      </div>
    </div>
  );
}

function ReferralsSection({ referrals, isEs, title, emptyText }) {
  const [openId, setOpenId] = useState(null);
  const [removed, setRemoved] = useState([]);
  const visible = referrals.filter((r) => !removed.includes(r.id));
  const removeRef = async (r) => {
    const msg = isEs
      ? `¿Eliminar a ${r.company || r.name || 'este referido'}?\n\nÚsalo si el restaurante ya no está interesado. Se notificará al equipo de Kitxens.`
      : `Remove ${r.company || r.name || 'this referral'}?\n\nUse this if the restaurant is no longer interested. The Kitxens team will be notified.`;
    if (!window.confirm(msg)) return;
    try {
      const res = await affFetch(`/me/referrals/${r.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      setRemoved((x) => [...x, r.id]);
    } catch {
      window.alert(isEs ? 'No se pudo eliminar. Los clientes convertidos no se pueden eliminar.' : 'Could not remove. Converted customers cannot be removed.');
    }
  };
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-6" data-testid="aff-referrals-table">
      <p className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{title}</p>
      <p className="mt-1 text-[12px] text-neutral-500">
        {isEs ? '🔍 Transparencia total: toca cualquier referido para ver cada acción que nuestro equipo ha hecho con tu lead.' : '🔍 Full transparency: tap any referral to see every action our team has taken on your lead.'}
      </p>
      <p className="mt-1 text-[11.5px] text-neutral-400">
        {isEs ? 'Los referidos sin conversión expiran automáticamente a los 90 días.' : 'Referrals with no conversion expire automatically after 90 days.'}
      </p>
      {visible.length === 0 ? <p className="mt-4 text-[13px] text-neutral-500">{emptyText}</p> : (
        <div className="mt-3 space-y-2">
          {visible.map((r) => (
            <div key={r.id} className="rounded-xl bg-neutral-50 border border-neutral-100 overflow-hidden">
              <div className="w-full flex items-center gap-3 px-3.5 py-2.5">
                <button type="button" onClick={() => setOpenId(openId === r.id ? null : r.id)}
                  className="flex-1 min-w-0 flex items-center gap-3 text-left" data-testid={`aff-ref-row-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-neutral-900 truncate">{r.company || r.name || r.email}</p>
                    <p className="text-[11px] text-neutral-500">{new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`text-[10px] font-bold uppercase rounded-full border px-2 py-0.5 ${REF_STATUS[r.status] || REF_STATUS.lead}`}>{r.status}</span>
                  <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${openId === r.id ? 'rotate-180' : ''}`} />
                </button>
                {r.status !== 'customer' && (
                  <button type="button" onClick={() => removeRef(r)}
                    title={isEs ? 'Eliminar referido' : 'Remove referral'}
                    className="p-1.5 rounded-lg text-neutral-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                    data-testid={`aff-ref-delete-${r.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              {openId === r.id && <ReferralTimeline rid={r.id} isEs={isEs} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Main page ---------- */
export default function AffiliateDashboardPage() {
  const { lang } = useT();
  const [theme, setTheme] = useOsTheme();
  const [prefLang, setPrefLang] = useState(null);
  const [state, setState] = useState('checking'); // checking | login | open
  const [data, setData] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('dashboard');
  const [cc, setCc] = useState(null);
  const isEs = (prefLang ?? data?.affiliate?.locale ?? lang) === 'es';

  useEffect(() => {
    if (state !== 'open' || data?.affiliate?.status !== 'approved') return;
    affFetch(`/me/command-center?lang=${isEs ? 'es' : 'en'}`)
      .then((r) => (r.ok ? r.json() : null)).then(setCc).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, isEs]);

  const switchLang = async (l) => {
    setPrefLang(l);
    try { await affFetch('/me/settings', { method: 'POST', body: JSON.stringify({ locale: l }) }); } catch { /* noop */ }
  };

  const c = isEs ? {
    title: 'Panel de Partner', login: 'Acceso de Partners', loginSub: 'Entra con el email y contraseña de tu solicitud.',
    enter: 'Entrar', apply: '¿Aún no formas parte del programa? Aplica aquí', logout: 'Salir',
    pendingT: 'Solicitud en revisión', pendingD: 'Tu solicitud está siendo revisada por nuestro equipo. Te contactaremos en 24-48h. Cuando se apruebe tu solicitud, aquí verás tu enlace de referido y tus estadísticas.',
    yourLink: 'Tu enlace de referido', copy: 'Copiar', copied: 'Enlace copiado ✅',
    clicks: 'Clics', referrals: 'Referidos generados', customers: 'Referidos exitosos',
    earned: 'Comisiones totales', pending: 'Pendiente de pago', paid: 'Pagado',
    refTable: 'Tus referidos', comTable: 'Historial de comisiones', payHistory: 'Historial de pagos', empty: 'Aún no hay datos — comparte tu enlace para empezar.',
    model: 'Ganas el 30% recurrente de todo lo que contrate tu referido. Tu referido recibe 10% de descuento durante 1 año.',
    errLogin: 'Credenciales incorrectas.',
    hi: 'Hola', navDash: 'Dashboard', navCom: 'Comisiones', navPay: 'Pagos', navRef: 'Referidos', navAssets: 'Biblioteca',
    payoutIntro: 'Configura dónde quieres cobrar y consulta el estado de tus pagos.',
    noPaypalWarn: 'Configura tu PayPal para poder recibir tus comisiones.',
    assetsIntro: 'Todo lo que necesitas para vender: herramientas, plantillas, QR, widgets y prospección.',
  } : {
    title: 'Partner Dashboard', login: 'Affiliate Access', loginSub: 'Sign in with the email and password from your application.',
    enter: 'Sign in', apply: 'Not an affiliate yet? Apply here', logout: 'Log out',
    pendingT: 'Application under review', pendingD: "Your application is being reviewed by our team. We'll reply within 24-48h. Once approved you'll see your referral link and stats here.",
    yourLink: 'Your referral link', copy: 'Copy', copied: 'Link copied ✅',
    clicks: 'Clicks', referrals: 'Referrals generated', customers: 'Successful referrals',
    earned: 'Total commissions', pending: 'Pending payout', paid: 'Paid',
    refTable: 'Your referrals', comTable: 'Commission history', payHistory: 'Payout history', empty: 'No data yet — share your link to get started.',
    model: 'You earn 30% recurring on everything your referral contracts. Your referral gets 10% off for 1 year.',
    errLogin: 'Invalid credentials.',
    hi: 'Hi', navDash: 'Dashboard', navCom: 'Commissions', navPay: 'Payouts', navRef: 'Referrals', navAssets: 'Assets Library',
    payoutIntro: 'Set up where you want to get paid and track your payout status.',
    noPaypalWarn: 'Set up your PayPal to be able to receive your commissions.',
    assetsIntro: 'Everything you need to sell: tools, templates, QR, widgets and prospecting.',
  };

  const load = useCallback(async () => {
    const r = await affFetch('/me');
    if (r.ok) { setData(await r.json()); setState('open'); }
    else setState('login');
  }, []);

  useEffect(() => {
    if (hasGoogleSessionHash()) {
      consumeGoogleSession('partner')
        .then(() => load())
        .catch((e) => {
          setState('login');
          setErr(e.detail === 'no_partner_account'
            ? (isEs ? 'Tu email de Google no está asociado a ningún partner aprobado.' : 'Your Google email is not linked to any approved partner.')
            : (isEs ? 'No pudimos verificar tu cuenta de Google.' : "We couldn't verify your Google account."));
        });
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const r = await affFetch('/login', { method: 'POST', body: JSON.stringify({ email, password }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(typeof d?.detail === 'string' ? d.detail : c.errLogin); setBusy(false); return; }
      await load();
    } catch { setErr(c.errLogin); }
    setBusy(false);
  };

  const doLogout = async () => {
    await affFetch('/logout', { method: 'POST' });
    setData(null); setState('login');
  };

  if (state === 'checking') {
    return <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center text-neutral-400 text-sm">…</div>;
  }

  if (state === 'login') {
    return (
      <AuthShell
        testId="affiliate-login"
        image="/auth/partner.png"
        eyebrow={isEs ? 'Acceso Partners Certificados' : 'Certified Partners Access'}
        title={isEs ? 'Tu negocio crece con cada restaurante que ayudas.' : 'Your business grows with every restaurant you help.'}
        sub={isEs ? 'Dashboard exclusivo para partners aprobados: comisiones recurrentes del 30%, kit de ventas y seguimiento de tus referidos.' : 'Exclusive dashboard for approved partners: 30% recurring commissions, sales kit and referral tracking.'}
        links={[
          [
            { q: isEs ? '¿Eres del equipo Kitxens?' : 'Kitxens team member?', href: '/login', label: isEs ? 'Accede al AI OS →' : 'Go to the AI OS →' },
            { q: isEs ? '¿Eres cliente?' : 'Are you a client?', href: '/account', label: isEs ? 'Tu área de cliente →' : 'Go to your client area →' },
          ],
          [
            { q: isEs ? '¿Aún no eres cliente?' : 'Not a customer?', href: '/demo', label: isEs ? '¡Empieza hoy! →' : 'Get started today! →' },
            { q: isEs ? '¿Quieres ser partner?' : 'Want to become a partner?', href: '/affiliates', label: isEs ? 'Únete a la comunidad →' : 'Join the Kitxens community →' },
          ],
        ]}
      >
        <SEO path="/affiliates/dashboard" title={`${c.title} | Kitxens`} noindex />
        <form onSubmit={doLogin} className="w-full max-w-sm rounded-2xl border border-neutral-200 bg-white p-8 shadow-sm">
          <div className="flex items-center gap-2.5">
            <img src="/kitxens-logo.png" alt="Kitxens" className="w-9 h-9 rounded-lg" />
            <p className="flex items-center gap-2 text-[#F77F11] text-[10px] tracking-[0.24em] uppercase font-bold"><Handshake className="w-3.5 h-3.5" /> Kitxens Partners</p>
          </div>
          <h1 className="mt-4 text-xl font-bold text-neutral-900">{c.login}</h1>
          <p className="mt-1 text-[14px] text-neutral-500">{c.loginSub}</p>
          <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setErr(''); }} placeholder="Email"
            className="mt-5 w-full h-11 rounded-xl bg-white border border-neutral-300 px-4 text-sm text-neutral-900 outline-none focus:border-[#F77F11]" data-testid="aff-login-email" />
          <input type="password" value={password} onChange={(e) => { setPassword(e.target.value); setErr(''); }} placeholder="Password"
            className="mt-3 w-full h-11 rounded-xl bg-white border border-neutral-300 px-4 text-sm text-neutral-900 outline-none focus:border-[#F77F11]" data-testid="aff-login-password" />
          {err && <p className="mt-2 text-[12px] text-red-500" data-testid="aff-login-error">{err}</p>}
          <button type="submit" disabled={busy} className="mt-4 w-full h-11 rounded-xl bg-[#F77F11] hover:bg-[#d8651a] disabled:opacity-60 text-white font-semibold text-sm transition-colors inline-flex items-center justify-center gap-2" data-testid="aff-login-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {c.enter}
          </button>
          <div className="mt-4 flex items-center gap-3" aria-hidden>
            <span className="h-px flex-1 bg-neutral-200" />
            <span className="text-[11px] text-neutral-400 uppercase">{isEs ? 'o' : 'or'}</span>
            <span className="h-px flex-1 bg-neutral-200" />
          </div>
          <button type="button" onClick={() => googleLoginRedirect('/affiliates/dashboard')}
            className="mt-4 w-full h-11 rounded-xl border border-neutral-300 bg-white hover:bg-neutral-50 text-neutral-800 font-semibold text-sm transition-colors inline-flex items-center justify-center gap-2.5"
            data-testid="aff-login-google-btn">
            <GoogleIcon /> {isEs ? 'Continuar con Google' : 'Continue with Google'}
          </button>
          <p className="mt-2 text-center text-[12.5px] text-neutral-500">
            {isEs ? 'Debe ser el email de tu cuenta de partner aprobada.' : 'Must be the email of your approved partner account.'}
          </p>
          <Link to="/affiliates" className="mt-4 block text-center text-[13px] text-neutral-500 hover:text-neutral-800" data-testid="aff-apply-link">{c.apply}</Link>
        </form>
      </AuthShell>
    );
  }

  const { affiliate, stats, referrals, commissions } = data;
  const link = `https://www.kitxens.com/?ref=${affiliate.ref_code}`;
  const paidCommissions = commissions.filter((cm) => cm.status === 'paid');

  const NAV = [
    { id: 'dashboard', label: c.navDash, icon: LayoutDashboard },
    { id: 'commissions', label: c.navCom, icon: Wallet },
    { id: 'payouts', label: c.navPay, icon: Banknote },
    { id: 'referrals', label: c.navRef, icon: Users },
    { id: 'assets', label: c.navAssets, icon: FolderOpen },
  ];

  const CommissionsList = ({ items, title, testId }) => (
    <div className="rounded-2xl border border-neutral-200 bg-white p-6" data-testid={testId}>
      <p className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{title}</p>
      {items.length === 0 ? <p className="mt-4 text-[13px] text-neutral-500">{c.empty}</p> : (
        <div className="mt-3 space-y-2">
          {items.map((cm) => (
            <div key={cm.id} className="flex items-center gap-3 rounded-xl bg-neutral-50 border border-neutral-100 px-3.5 py-2.5">
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-neutral-900 truncate">{cm.description || cm.period || '—'}</p>
                <p className="text-[11px] text-neutral-500">{cm.period} · {new Date(cm.created_at).toLocaleDateString()}</p>
              </div>
              <p className="font-bold tabular-nums text-[14px] text-neutral-900">{money(cm.amount)}</p>
              <span className={`text-[10px] font-bold uppercase rounded-full border px-2 py-0.5 ${cm.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{cm.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen os3-scope bg-[#FAFAF8] pt-32 md:pt-36 pb-16" data-testid="affiliate-dashboard">
      <SEO path="/affiliates/dashboard" title={`${c.title} | Kitxens`} noindex />
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
        {affiliate.status === 'pending' ? (
          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-8" data-testid="aff-pending-banner">
            <p className="flex items-center gap-2 text-amber-700 font-bold"><Clock className="w-4 h-4" /> {c.pendingT}</p>
            <p className="mt-2 text-[13.5px] text-neutral-600 leading-relaxed max-w-xl">{c.pendingD}</p>
            <button onClick={doLogout} className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-neutral-300 px-4 h-9 text-[12px] font-semibold text-neutral-600 hover:border-red-300 hover:text-red-600 transition-colors" data-testid="aff-logout">
              <LogOut className="w-3.5 h-3.5" /> {c.logout}
            </button>
          </div>
        ) : (
          <div className="lg:grid lg:grid-cols-[230px,1fr] lg:gap-6 lg:items-start">
            {/* ---------- Sidebar (desktop) ---------- */}
            <aside className="hidden lg:flex flex-col sticky top-32 rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm" data-testid="aff-sidebar">
              <div className="flex items-center gap-2.5 px-2 pt-1">
                <img src="/kitxens-logo.png" alt="Kitxens" className="w-9 h-9 rounded-lg" data-testid="aff-sidebar-logo" />
                <div className="min-w-0">
                  <p className="text-[13px] font-bold text-neutral-900 leading-tight">Kitxens</p>
                  <p className="text-[10px] uppercase tracking-wider text-[#F77F11] font-bold">Partners</p>
                </div>
              </div>
              <div className="mt-4 px-2">
                <p className="text-[12.5px] font-semibold text-neutral-800 truncate">👋 {affiliate.name}</p>
                {stats.tier?.tier_name && <p className="text-[11px] text-neutral-500">{stats.tier.tier_name} · {Math.round((stats.tier.rate || 0.3) * 100)}%</p>}
                <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#F77F11]/10 border border-[#F77F11]/25 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-[#d8651a]" data-testid="aff-certified-badge">
                  <BadgeCheck className="w-3 h-3" /> Certified Partner
                </span>
              </div>
              <nav className="mt-4 space-y-1">
                {NAV.map((n) => (
                  <button key={n.id} onClick={() => { setTab(n.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className={`w-full flex items-center gap-2.5 rounded-xl px-3 h-10 text-[13px] font-semibold transition-colors ${tab === n.id ? 'bg-[#F77F11]/10 text-[#d8651a]' : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'}`}
                    data-testid={`aff-tab-${n.id}`}>
                    <n.icon className={`w-4 h-4 ${tab === n.id ? 'text-[#F77F11]' : 'text-neutral-400'}`} /> {n.label}
                  </button>
                ))}
              </nav>
              <div className="mt-5 pt-4 border-t border-neutral-100 flex flex-col items-center text-center">
                <div className="flex flex-wrap items-center justify-center gap-1.5">
                  <div className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-neutral-50 p-1" data-testid="aff-lang-toggle">
                    {['en', 'es'].map((l) => (
                      <button key={l} onClick={() => switchLang(l)} data-testid={`aff-lang-${l}`}
                        className={`px-3 h-7 rounded-full text-[11px] font-bold uppercase transition-colors ${(isEs ? 'es' : 'en') === l ? 'bg-[#F77F11] text-white' : 'text-neutral-500 hover:text-neutral-900'}`}>
                        {l.toUpperCase()}
                      </button>
                    ))}
                  </div>
                  <TextSizePill isEs={isEs} compact />
                  <ThemeToggle theme={theme} setTheme={setTheme} isEs={isEs} />
                </div>
                <button onClick={doLogout} className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl px-3 h-10 text-[13px] font-semibold text-neutral-500 hover:bg-red-50 hover:text-red-600 transition-colors" data-testid="aff-logout-desktop">
                  <LogOut className="w-4 h-4" /> {c.logout}
                </button>
              </div>
            </aside>

            {/* ---------- Content ---------- */}
            <main className="min-w-0">
              {/* Mobile top bar */}
              <div className="lg:hidden mb-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <img src="/kitxens-logo.png" alt="Kitxens" className="w-8 h-8 rounded-lg" />
                    <div>
                      <p className="text-[13px] font-bold text-neutral-900 leading-tight">Kitxens Partners</p>
                      <p className="text-[11px] text-neutral-500">👋 {affiliate.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="inline-flex items-center rounded-full border border-neutral-200 bg-white p-0.5">
                      {['en', 'es'].map((l) => (
                        <button key={l} onClick={() => switchLang(l)}
                          className={`px-2.5 h-6 rounded-full text-[10px] font-bold uppercase ${(isEs ? 'es' : 'en') === l ? 'bg-[#F77F11] text-white' : 'text-neutral-500'}`}>
                          {l.toUpperCase()}
                        </button>
                      ))}
                    </div>
                    <button onClick={doLogout} className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-neutral-200 text-neutral-500 hover:text-red-600 hover:border-red-200 transition-colors" data-testid="aff-logout-mobile">
                      <LogOut className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1 -mx-4 px-4" data-testid="aff-mobile-tabs">
                  {NAV.map((n) => (
                    <button key={n.id} onClick={() => setTab(n.id)}
                      className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 h-9 text-[12.5px] font-semibold border transition-colors ${tab === n.id ? 'bg-[#F77F11] border-[#F77F11] text-white' : 'bg-white border-neutral-200 text-neutral-600'}`}
                      data-testid={`aff-mtab-${n.id}`}>
                      <n.icon className="w-3.5 h-3.5" /> {n.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* ===== Dashboard — Sales Operating Center ===== */}
              {tab === 'dashboard' && (
                <div data-testid="aff-panel-dashboard">
                  <div className="mb-4">
                    <RachelGreeting isEs={isEs} name={affiliate.name} testid="aff-rachel" compact
                      msgEs={<>Tienes <b>{stats.referrals ?? 0}</b> {(stats.referrals ?? 0) === 1 ? 'referido' : 'referidos'} y <b>{stats.clicks ?? 0}</b> clics en tu enlace.{(stats.pending_total ?? 0) > 0 ? <> Llevas <b>{money(stats.pending_total)}</b> en comisiones pendientes de pago — buen trabajo.</> : (stats.referrals ?? 0) === 0 ? ' Comparte tu enlace hoy para generar tus primeros referidos — te preparé mensajes listos en Sales Toolkit.' : ''}</>}
                      msgEn={<>You have <b>{stats.referrals ?? 0}</b> {(stats.referrals ?? 0) === 1 ? 'referral' : 'referrals'} and <b>{stats.clicks ?? 0}</b> link clicks.{(stats.pending_total ?? 0) > 0 ? <> You've got <b>{money(stats.pending_total)}</b> in commissions pending payout — nice work.</> : (stats.referrals ?? 0) === 0 ? ' Share your link today to generate your first referrals — I prepared ready-made messages in the Sales Toolkit.' : ''}</>} />
                  </div>
                  <PartnerHero cc={cc} isEs={isEs} tierName={stats.tier?.tier_name || 'Bronze'} rate={stats.tier?.rate} />
                  <EvaTip cc={cc} isEs={isEs} />
                  <TodayMission cc={cc} isEs={isEs} onUpdate={(m) => setCc((p) => ({ ...p, mission: m }))} />
                  <QuickActions isEs={isEs} refCode={affiliate.ref_code} name={affiliate.name}
                    onGoAssets={() => { setTab('assets'); setTimeout(() => document.querySelector('[data-testid="aff-proposal-gen"]')?.scrollIntoView({ behavior: 'smooth' }), 300); }} />
                  <div className="mt-4"><LinkBox link={link} c={c} /></div>
                  <TrackingPanel isEs={isEs} refCode={affiliate.ref_code} />
                  <PartnerKitxensEffect isEs={isEs} />
                  <PartnerScans isEs={isEs} />
                  <TodayStats cc={cc} isEs={isEs} />
                  <NearbyRadar cc={cc} isEs={isEs} refCode={affiliate.ref_code}
                    onGoAssets={() => { setTab('assets'); setTimeout(() => document.querySelector('[data-testid="aff-opportunity-map"]')?.scrollIntoView({ behavior: 'smooth' }), 300); }} />
                  <DemoRadar isEs={isEs} refCode={affiliate.ref_code} stats={stats} />
                  {!affiliate.paypal_email && (
                    <button onClick={() => setTab('payouts')} className="mt-4 w-full text-left rounded-2xl border border-[#0070BA]/25 bg-[#0070BA]/[0.05] p-4 flex items-center gap-3 hover:border-[#0070BA]/50 transition-colors" data-testid="aff-paypal-nudge">
                      <Banknote className="w-5 h-5 text-[#0070BA] shrink-0" />
                      <span className="text-[13px] text-neutral-700 font-medium">{c.noPaypalWarn} <span className="text-[#0070BA] font-bold">→</span></span>
                    </button>
                  )}
                  <DailyIdea isEs={isEs} />
                  <PartnerRadar isEs={isEs} />
                  <PartnerTier tier={stats.tier} isEs={isEs} />
                  <AttributionFlow isEs={isEs} />
                  <PartnerLeaderboard isEs={isEs} dark={false} />
                  <EstimateDisclaimer isEs={isEs} testid="aff-estimate-disclaimer" />
                </div>
              )}

              {/* ===== Commissions ===== */}
              {tab === 'commissions' && (
                <div data-testid="aff-panel-commissions">
                  <h1 className="text-2xl font-bold tracking-[-0.02em] text-neutral-900">{c.navCom}</h1>
                  <p className="mt-1 text-[13px] text-neutral-500">{c.model}</p>
                  <div className="mt-5">
                    <StatCards testId="aff-com-stats" cards={[
                      { icon: Wallet, l: c.earned, v: money(stats.earned_total) },
                      { icon: Clock, l: c.pending, v: money(stats.pending_total) },
                      { icon: CheckCircle2, l: c.paid, v: money(stats.paid_total) },
                    ]} />
                  </div>
                  <div className="mt-4"><CommissionsList items={commissions} title={c.comTable} testId="aff-commissions-table" /></div>
                  <PartnerTier tier={stats.tier} isEs={isEs} />
                </div>
              )}

              {/* ===== Payouts ===== */}
              {tab === 'payouts' && (
                <div data-testid="aff-panel-payouts">
                  <h1 className="text-2xl font-bold tracking-[-0.02em] text-neutral-900">{c.navPay}</h1>
                  <p className="mt-1 text-[13px] text-neutral-500">{c.payoutIntro}</p>
                  <div className="mt-5">
                    <StatCards testId="aff-pay-stats" cards={[
                      { icon: Clock, l: c.pending, v: money(stats.pending_total) },
                      { icon: CheckCircle2, l: c.paid, v: money(stats.paid_total) },
                      { icon: Wallet, l: c.earned, v: money(stats.earned_total) },
                    ]} />
                  </div>
                  <div className="mt-4">
                    <PayoutSettings affiliate={affiliate} isEs={isEs} onSaved={load} />
                  </div>
                  <div className="mt-4"><CommissionsList items={paidCommissions} title={c.payHistory} testId="aff-payout-history" /></div>
                </div>
              )}

              {/* ===== Referrals ===== */}
              {tab === 'referrals' && (
                <div data-testid="aff-panel-referrals">
                  <h1 className="text-2xl font-bold tracking-[-0.02em] text-neutral-900">{c.navRef}</h1>
                  <p className="mt-1 text-[13px] text-neutral-500">{c.model}</p>
                  <div className="mt-5"><ReferralsSection referrals={referrals} isEs={isEs} title={c.refTable} emptyText={c.empty} /></div>
                  <TrackingPanel isEs={isEs} refCode={affiliate.ref_code} />
                  <AttributionFlow isEs={isEs} />
                  <TrustPanel isEs={isEs} />
                  <AffiliateActivity isEs={isEs} />
                </div>
              )}

              {/* ===== Assets Library ===== */}
              {tab === 'assets' && (
                <div data-testid="aff-panel-assets">
                  <h1 className="text-2xl font-bold tracking-[-0.02em] text-neutral-900">{c.navAssets}</h1>
                  <p className="mt-1 text-[13px] text-neutral-500">{c.assetsIntro}</p>
                  <div className="mt-5"><LinkBox link={link} c={c} /></div>
                  <IndexAiScanPromo isEs={isEs} refCode={affiliate.ref_code} />
                  <EmailSignature isEs={isEs} affiliate={affiliate} />
                  <BrandKit isEs={isEs} />
                  <PartnerKit link={link} refCode={affiliate.ref_code} name={affiliate.name} isEs={isEs} />
                  <OpportunityMap isEs={isEs} refLink={link} />
                  <ProposalGenerator isEs={isEs} />
                  <AffiliateToolkit isEs={isEs} />
                  <SalesToolkit isEs={isEs} link={link} name={affiliate.name.split(' ')[0]} refCode={affiliate.ref_code} />
                </div>
              )}
            </main>
          </div>
        )}
      </div>
    </div>
  );
}
